import { DataFeedSubscription } from '@tradingblock/types';
import { getNewQuoteSubscription, getNewSpreadsSubscription } from './DataFeed';
import { getQuoteSubscription as getOldQuoteSubscription  } from './DataFeed';

export interface SubscriptionContext {
  apiToken: string;
  trySend: (cmd: string | undefined) => boolean;
  trySendQuotes: (cmd: any) => boolean;
  trySendSpreads: (cmd: any) => boolean;
  isMarketCurrentlyOpen?: boolean;
}

export interface SubscriptionResult {
  success: boolean;
  service: string;
  error?: string;
}

export class SubscriptionResult {
  constructor(
    public success: boolean,
    public service: string,
    public error?: string
  ) {}

  static success(service: string): SubscriptionResult {
    return new SubscriptionResult(true, service);
  }

  static failure(service: string, error: string): SubscriptionResult {
    return new SubscriptionResult(false, service, error);
  }
}

export interface SubscriptionStrategy {
  name: string;
  subscribe(accountId: string | number, subscription: DataFeedSubscription[]): SubscriptionResult;
  unsubscribe(accountId: string | number, subscription: DataFeedSubscription[]): SubscriptionResult;
}

export class OldWebSocketStrategy implements SubscriptionStrategy {
  public readonly name = 'OldWebSocket';

  constructor(private context: SubscriptionContext) {}

  subscribe(accountId: string | number, subscription: DataFeedSubscription[]): SubscriptionResult {
    try {
      const cmd = getOldQuoteSubscription(this.context.apiToken, accountId, subscription, false);
      if (cmd) {
        const success = this.context.trySend(cmd);
        return success 
          ? SubscriptionResult.success(this.name)
          : SubscriptionResult.failure(this.name, 'Failed to send subscription command');
      }
      return SubscriptionResult.failure(this.name, 'No command generated');
    } catch (error) {
      return SubscriptionResult.failure(this.name, error instanceof Error ? error.message : 'Unknown error');
    }
  }

  unsubscribe(accountId: string | number, subscription: DataFeedSubscription[]): SubscriptionResult {
    try {
      const cmd = getOldQuoteSubscription(this.context.apiToken, accountId, subscription, true);
      if (cmd) {
        const success = this.context.trySend(cmd);
        return success 
          ? SubscriptionResult.success(this.name)
          : SubscriptionResult.failure(this.name, 'Failed to send unsubscription command');
      }
      return SubscriptionResult.failure(this.name, 'No command generated');
    } catch (error) {
      return SubscriptionResult.failure(this.name, error instanceof Error ? error.message : 'Unknown error');
    }
  }
}

export class NewQuotesStrategy implements SubscriptionStrategy {
  public readonly name = 'NewQuotes';

  constructor(private context: SubscriptionContext) {}

  subscribe(accountId: string | number, subscription: DataFeedSubscription[]): SubscriptionResult {
    try {
      const cmd = getNewQuoteSubscription(this.context.apiToken, accountId, subscription, false);
      if (cmd && cmd.symbols && cmd.symbols.length > 0) {
        const success = this.context.trySendQuotes(cmd);
        return success 
          ? SubscriptionResult.success(this.name)
          : SubscriptionResult.failure(this.name, 'Failed to send quotes subscription command');
      }
      return SubscriptionResult.failure(this.name, 'No valid symbols in subscription');
    } catch (error) {
      return SubscriptionResult.failure(this.name, error instanceof Error ? error.message : 'Unknown error');
    }
  }

  unsubscribe(accountId: string | number, subscription: DataFeedSubscription[]): SubscriptionResult {
    try {
      const cmd = getNewQuoteSubscription(this.context.apiToken, accountId, subscription, true);
      if (cmd && cmd.symbols && cmd.symbols.length > 0) {
        const success = this.context.trySendQuotes(cmd);
        return success 
          ? SubscriptionResult.success(this.name)
          : SubscriptionResult.failure(this.name, 'Failed to send quotes unsubscription command');
      }
      return SubscriptionResult.failure(this.name, 'No valid symbols in subscription');
    } catch (error) {
      return SubscriptionResult.failure(this.name, error instanceof Error ? error.message : 'Unknown error');
    }
  }
}

export class NewSpreadsStrategy implements SubscriptionStrategy {
  public readonly name = 'NewSpreads';

  constructor(private context: SubscriptionContext) {}

  subscribe(accountId: string | number, subscription: DataFeedSubscription[]): SubscriptionResult {
    try {
      const cmd = getNewSpreadsSubscription(this.context.apiToken, accountId, subscription);
      if (cmd && cmd.spreads && cmd.spreads.length > 0) {
        const success = this.context.trySendSpreads(cmd);
        return success 
          ? SubscriptionResult.success(this.name)
          : SubscriptionResult.failure(this.name, 'Failed to send spreads subscription command');
      }
      return SubscriptionResult.failure(this.name, 'No valid spreads in subscription');
    } catch (error) {
      return SubscriptionResult.failure(this.name, error instanceof Error ? error.message : 'Unknown error');
    }
  }

  unsubscribe(accountId: string | number, subscription: DataFeedSubscription[]): SubscriptionResult {
    try {
      const cmd = getNewSpreadsSubscription(this.context.apiToken, accountId, subscription, true);
      if (cmd && cmd.spreads && cmd.spreads.length > 0) {
        const success = this.context.trySendSpreads(cmd);
        return success 
          ? SubscriptionResult.success(this.name)
          : SubscriptionResult.failure(this.name, 'Failed to send spreads unsubscription command');
      }
      return SubscriptionResult.failure(this.name, 'No valid spreads in subscription');
    } catch (error) {
      return SubscriptionResult.failure(this.name, error instanceof Error ? error.message : 'Unknown error');
    }
  }
}

export function getSubscriptionStrategy(
  subscription: DataFeedSubscription[],
  context: SubscriptionContext,
  useNewQuoteService: boolean,
  useNewSpreadService: boolean
): SubscriptionStrategy {
  const hasSpreads = subscription.some(s => s.kind === 'Spread');
  
  if (hasSpreads) {
    return useNewSpreadService 
      ? new NewSpreadsStrategy(context)
      : new OldWebSocketStrategy(context);
  } else {
    return useNewQuoteService
      ? new NewQuotesStrategy(context)
      : new OldWebSocketStrategy(context);
  }
}
